import { usePageTitle } from '@/layouts/usePageTitle';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Gauge,
  Thermometer,
  Wind,
  Compass,
  Sun,
  Clock3,
  PlugZap,
  Network,
  ChevronRight,
} from 'lucide-react';
import {
  fetchSensorDetail,
  type SensorDetail,
  type SensorPlant,
} from '@/api/sensors';
import { useI18n } from '@/i18n';
import { useErrorToast, LoadRetry } from '@/lib/notify';

function StatusBadge({ status }: { status: number }) {
  const { t } = useI18n();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        status === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
      }`}
    >
      {status === 1 ? t('common.online') : t('common.offline')}
    </span>
  );
}

function valueOrDash(value: number | string | null | undefined, suffix?: string): string {
  if (value === null || value === undefined) return '—';
  return suffix ? `${value} ${suffix}` : String(value);
}

function MetricCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs text-gray-500">
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-sm font-semibold text-gray-800">{value}</p>
    </div>
  );
}

export default function SensorDetailPage() {
  const { t } = useI18n();
  const { uuid_id } = useParams<{ uuid_id: string }>();

  const [sensor, setSensor] = useState<SensorDetail | null>(null);
  const [plants, setPlants] = useState<SensorPlant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  usePageTitle(sensor && sensor.sensor_uuid_id === uuid_id ? sensor.sensor_name : null);

  useEffect(() => {
    if (!uuid_id) return;
    fetchSensorDetail(uuid_id)
      .then((data) => {
        setSensor(data.sensor);
        setPlants(data.plants);
      })
      .catch(() => setError(t('sensors.detailLoadError')))
      .finally(() => setLoading(false));
  }, [uuid_id, t]);

  if (loading) return <div className="p-4 text-sm text-gray-500">{t('common.loading')}</div>;
  if (error) return <LoadRetry />;
  if (!sensor) return <div className="p-4 text-sm text-rose-600">{t('sensors.notFound')}</div>;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-5 overflow-y-auto pb-4">
      <div className="space-y-4 rounded-xl border bg-white p-4 shadow-sm md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={sensor.sensor_status} />
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
            {[sensor.sensor_brand, sensor.sensor_model]
              .map((value) => value?.trim())
              .filter((value) => value && value.toLowerCase() !== 'other')
              .join(' ') || t('sensors.brandModelUnspecified')}
          </span>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <MetricCard
            icon={<Gauge className="h-3.5 w-3.5" />}
            label={t('sensors.serial')}
            value={valueOrDash(sensor.sensor_serial_number)}
          />
          <MetricCard
            icon={<Network className="h-3.5 w-3.5" />}
            label={t('sensors.powerPlant')}
            value={sensor.sensor_power_plant_name}
          />
          <MetricCard
            icon={<Clock3 className="h-3.5 w-3.5" />}
            label={t('common.lastSeen')}
            value={valueOrDash(sensor.sensor_last_seen)}
          />
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <MetricCard
            icon={<Thermometer className="h-3.5 w-3.5" />}
            label={t('sensors.pvTemp')}
            value={valueOrDash(sensor.sensor_pv_temp, '°C')}
          />
          <MetricCard
            icon={<Thermometer className="h-3.5 w-3.5" />}
            label={t('sensors.ambientTemp')}
            value={valueOrDash(sensor.sensor_amp_temp, '°C')}
          />
          <MetricCard
            icon={<Sun className="h-3.5 w-3.5" />}
            label={t('sensors.radiation')}
            value={valueOrDash(sensor.sensor_radiation, 'W/m²')}
          />
          <MetricCard
            icon={<Wind className="h-3.5 w-3.5" />}
            label={t('sensors.windSpeed')}
            value={valueOrDash(sensor.sensor_wind_speed, 'm/s')}
          />
          <MetricCard
            icon={<Compass className="h-3.5 w-3.5" />}
            label={t('sensors.windDirection')}
            value={valueOrDash(sensor.sensor_wind_direction, '°')}
          />
          <MetricCard
            icon={<Sun className="h-3.5 w-3.5" />}
            label={t('sensors.dailyIrradiation')}
            value={valueOrDash(sensor.sensor_daily_irradiation_kwh, 'kWh/m²')}
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <MetricCard
            icon={<PlugZap className="h-3.5 w-3.5" />}
            label={t('sensors.ftpStatus')}
            value={sensor.sensor_ftp_connection_status === 1 ? t('sensors.connected') : t('sensors.disconnected')}
          />
          <MetricCard
            icon={<PlugZap className="h-3.5 w-3.5" />}
            label={t('sensors.modbusStatus')}
            value={sensor.sensor_modbus_connection_status === 1 ? t('sensors.connected') : t('sensors.disconnected')}
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">{t('sensors.usedInPlants')}</h2>
        <span className="text-sm text-gray-400">{t('common.records', { count: plants.length })}</span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {plants.map(plant => (
          <Link
            key={plant.sensor_power_plant_uuid_id}
            to={`/dashboard/powerplants/${plant.sensor_power_plant_uuid_id}`}
            className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-blue-300 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
          >
            <span className="rounded-lg bg-blue-50 p-2.5 text-blue-600"><Network className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1 break-words text-sm font-semibold text-gray-800">{plant.sensor_power_plant_name}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 group-hover:text-blue-600" />
          </Link>
        ))}
        {plants.length === 0 && (
          <p className="col-span-full rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
            Bağlı aktif santral bulunamadı.
          </p>
        )}
      </div>
    </div>
  );
}
